import * as React from "react";
import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import Dialog from "src/components/Dialog";
import WeddingPartyModal from "../WeddingPartyModal";
import { GetWeddingPartyQuery } from "@src/@types/api.d";
import { motion } from "framer-motion";
import flowerBackground from "../../../../public/images/bw-flower.jpeg";

export type IWeddingParty = {
  eventId: number;
  weddingPartyData: GetWeddingPartyQuery;
  loading?: boolean;
  sectionImage?: string;
};

export type WeddingPartyType = NonNullable<
  GetWeddingPartyQuery["getWeddingParty"]
>[number];

const WeddingParty: React.FC<IWeddingParty> = ({
  eventId,
  weddingPartyData,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Set initial value
    setIsMobile(typeof window !== "undefined" && window.innerWidth < 768);

    // Handle resize
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const weddingPartyMembers = (weddingPartyData?.getWeddingParty || []).filter(
    (member): member is WeddingPartyType =>
      member !== null &&
      !!member.first_name &&
      !!member.last_name &&
      !!member.role
  );

  const sortedMembers = [...weddingPartyMembers].sort((a, b) => {
    if (a?.order_index !== null && b?.order_index !== null) {
      return a?.order_index! - b?.order_index!;
    }
    if (a?.order_index !== null && b?.order_index === null) return -1;
    if (a?.order_index === null && b?.order_index !== null) return 1;
    return 0;
  });

  const [currentPage, setCurrentPage] = useState(0);
  const CARDS_PER_PAGE = 3;
  const totalPages = Math.ceil(sortedMembers.length / CARDS_PER_PAGE);

  const scrollToSection = () => {
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      scrollToSection();
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      scrollToSection();
    }
  };

  if (!sortedMembers.length) return null;

  const startIdx = currentPage * CARDS_PER_PAGE;
  const visibleMembers = sortedMembers.slice(
    startIdx,
    startIdx + CARDS_PER_PAGE
  );
  const desktopGridColsClass =
    visibleMembers.length === 1
      ? "grid-cols-1"
      : visibleMembers.length === 2
      ? "grid-cols-2"
      : "grid-cols-3";

  const renderCard = (member: WeddingPartyType) => {
    const cardContent = (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col w-full md:w-80 lg:w-[360px]"
      >
        <div
          className={`flex flex-col bg-[#FAF7F5]/95 border border-[#228B22]/18 overflow-hidden transition-all duration-300 relative shadow-[0_18px_45px_rgba(28,24,32,0.08)]
         h-[450px] md:h-[500px] hover:-translate-y-1`}
        >
          <div className="relative w-full h-80 overflow-hidden bg-transparent">
            <Image
              placeholder="blur"
              blurDataURL={`data:image/svg+xml;base64,${toBase64(
                shimmer(300, 400)
              )}`}
              layout="fill"
              objectFit="cover"
              objectPosition="top"
              src={member?.image_url || flowerBackground}
              alt={`${member?.first_name} ${member?.last_name}`}
              className="transition-transform duration-300 hover:scale-105"
              onError={(event) => {
                const target = event.currentTarget as HTMLImageElement;
                if (!target.src.includes("bw-flower.jpeg")) {
                  target.src = flowerBackground.src;
                }
              }}
            />
          </div>

          <div className="p-4 md:p-5 flex flex-col justify-start flex-1">
            <h3 className="text-2xl md:text-3xl font-normal text-[#401d15] md:mb-1 leading-tight font-corGaramond">
              {member?.first_name}{" "}
              {member?.nickname ? `"${member?.nickname}"` : ""}{" "}
              {member?.last_name}
            </h3>
            <p
              className={`${
                member?.role.length ?? 0 > 12 ? "text-xs" : "text-sm"
              } md:text-base text-[#228B22] mb-1 md:mb-2 uppercase tracking-widest font-medium font-jost`}
            >
              {member?.role}
            </p>
            {member?.story && (
              <article
                className="text-base md:text-lg font-light text-[#401d15]/60 font-corGaramond w-full leading-relaxed"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: isMobile ? 2 : 3,
                  lineClamp: isMobile ? 2 : 3,
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  minHeight: 0,
                  maxHeight: isMobile ? "4em" : "4.5em",
                }}
              >
                {member.story}
              </article>
            )}
          </div>
        </div>
      </motion.div>
    );

    return (
      <Dialog
        key={member?.id}
        trigger={cardContent}
        onOpenChange={setIsModalOpen}
      >
        <WeddingPartyModal
          name={
            member?.nickname
              ? `${member?.first_name} "${member?.nickname}" ${member?.last_name}`
              : `${member?.first_name} ${member?.last_name}`
          }
          nickname={undefined}
          role={member?.role ?? ""}
          story={member?.story || ""}
          imageUrl={member?.image_url || ""}
        />
      </Dialog>
    );
  };

  return (
    <div
      ref={sectionRef}
      className="w-full py-10 md:py-16 overflow-hidden relative bg-[#FAF7F5]"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(34,139,34,0.08),transparent_30%),radial-gradient(circle_at_82%_18%,rgba(28,24,32,0.05),transparent_28%)]" />
      <div className="mb-12 text-center relative z-10 md:mb-16">
        <span className="mb-4 block font-jost text-[0.62rem] font-medium uppercase tracking-[0.35em] text-[#228B22]">
          Our People
        </span>
        <h3 className="font-corGaramond text-[clamp(3rem,7vw,5.4rem)] font-normal leading-[1.02] text-[#401d15]">
          Wedding <em className="font-light italic text-[#228B22]">Party</em>
        </h3>
        <div className="mt-5 flex items-center justify-center gap-4">
          <span className="h-px w-[56px] bg-gradient-to-r from-transparent to-[#228B22]" />
          <span className="h-1.5 w-1.5 rotate-45 bg-[#228B22]" />
          <span className="h-px w-[56px] bg-gradient-to-r from-[#228B22] to-transparent" />
        </div>
      </div>

      {/* Mobile: Single column scrollable */}
      <div className="md:hidden px-4 pb-8">
        <div className="space-y-6">
          {visibleMembers.map((member) => renderCard(member))}
        </div>

        {/* Bottom chevrons for mobile */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <motion.button
              onClick={goToPrevPage}
              disabled={currentPage === 0}
              className="w-[46px] h-[46px] rounded-full border border-[#228B22] bg-transparent text-[#228B22] flex items-center justify-center transition-all duration-200 hover:bg-[#228B22] hover:text-[#FAF7F5] hover:scale-105 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Previous"
            >
              <svg
                width="24"
                height="20"
                viewBox="0 0 56 87"
                xmlns="http://www.w3.org/2000/svg"
                style={{ transform: "scaleX(-1)" }}
              >
                <g>
                  <line
                    transform="rotate(101.38104248046875 27.75 63.25)"
                    y2="83.75"
                    x2="53.75"
                    y1="42.75"
                    x1="1.75"
                    strokeWidth="5"
                    stroke="currentColor"
                    fill="none"
                  />
                  <line
                    y2="41.75"
                    x2="52.75"
                    y1="0.75"
                    x1="0.75"
                    strokeWidth="5"
                    stroke="currentColor"
                    fill="none"
                  />
                </g>
              </svg>
            </motion.button>
            <motion.button
              onClick={goToNextPage}
              disabled={currentPage === totalPages - 1}
              className="w-[46px] h-[46px] rounded-full border border-[#228B22] bg-transparent text-[#228B22] flex items-center justify-center transition-all duration-200 hover:bg-[#228B22] hover:text-[#FAF7F5] hover:scale-105 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Next"
            >
              <svg
                width="24"
                height="20"
                viewBox="0 0 56 87"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g>
                  <line
                    transform="rotate(101.38104248046875 27.75 63.25)"
                    y2="83.75"
                    x2="53.75"
                    y1="42.75"
                    x1="1.75"
                    strokeWidth="5"
                    stroke="currentColor"
                    fill="none"
                  />
                  <line
                    y2="41.75"
                    x2="52.75"
                    y1="0.75"
                    x1="0.75"
                    strokeWidth="5"
                    stroke="currentColor"
                    fill="none"
                  />
                </g>
              </svg>
            </motion.button>
          </div>
        )}
      </div>

      {/* Desktop: 3-column grid */}
      <div className="hidden md:block max-w-7xl mx-auto md:border border-[#228B22]/40 p-2 relative z-10">
        <div className="bg-transparent md:bg-[#FAF7F5]/95 md:border border-[#228B22]/40 p-4 md:p-6 shadow-[0_24px_70px_rgba(28,24,32,0.08)]">
          <div className="flex items-center justify-between gap-6">
            {/* Left chevron */}
            {totalPages > 1 && (
              <motion.button
                onClick={goToPrevPage}
                disabled={currentPage === 0}
                className="flex-shrink-0 w-[46px] h-[46px] rounded-full border border-[#228B22] bg-transparent text-[#228B22] flex items-center justify-center transition-all duration-200 hover:bg-[#228B22] hover:text-[#FAF7F5] hover:scale-105 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Previous group"
              >
                <svg
                  width="24"
                  height="20"
                  viewBox="0 0 56 87"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ transform: "scaleX(-1)" }}
                >
                  <g>
                    <line
                      transform="rotate(101.38104248046875 27.75 63.25)"
                      y2="83.75"
                      x2="53.75"
                      y1="42.75"
                      x1="1.75"
                      strokeWidth="5"
                      stroke="currentColor"
                      fill="none"
                    />
                    <line
                      y2="41.75"
                      x2="52.75"
                      y1="0.75"
                      x1="0.75"
                      strokeWidth="5"
                      stroke="currentColor"
                      fill="none"
                    />
                  </g>
                </svg>
              </motion.button>
            )}

            {/* Grid of cards */}
            <div className="flex-1 flex justify-center">
              <div
                className={`grid ${desktopGridColsClass} gap-8 lg:gap-10 w-fit justify-items-center`}
              >
                {visibleMembers.map((member) => renderCard(member))}
              </div>
            </div>

            {/* Right chevron */}
            {totalPages > 1 && (
              <motion.button
                onClick={goToNextPage}
                disabled={currentPage === totalPages - 1}
                className="flex-shrink-0 w-[46px] h-[46px] rounded-full border border-[#228B22] bg-transparent text-[#228B22] flex items-center justify-center transition-all duration-200 hover:bg-[#228B22] hover:text-[#FAF7F5] hover:scale-105 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Next group"
              >
                <svg
                  width="24"
                  height="20"
                  viewBox="0 0 56 87"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g>
                    <line
                      transform="rotate(101.38104248046875 27.75 63.25)"
                      y2="83.75"
                      x2="53.75"
                      y1="42.75"
                      x1="1.75"
                      strokeWidth="5"
                      stroke="currentColor"
                      fill="none"
                    />
                    <line
                      y2="41.75"
                      x2="52.75"
                      y1="0.75"
                      x1="0.75"
                      strokeWidth="5"
                      stroke="currentColor"
                      fill="none"
                    />
                  </g>
                </svg>
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeddingParty;
